import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  dateFormat,
  splitMissionName,
} from "../../../../services/Utils/functionService";
import { useCustomerMissionStore } from "../../../../stores/Cac/Customer/customerMission";
import useSorting from "../../../../hooks/useSorting";
import { useState } from "react";
import Return from "../../../../assets/icons/return.svg";

const CustomerMissionTable = () => {
  const { t } = useTranslation();
  // Store
  const { missionsClient, setIsRenewable } = useCustomerMissionStore();

  // Hooks
  const navigate = useNavigate();
  const [sortedData, handleSort] = useSorting(missionsClient);
  const { idCustomer } = useParams();

  // State pour le tri
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Fonction de tri personnalisée
  const handleCustomSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    handleSort(field);
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortDirection === "asc") {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Header avec système de tri moderne */}
      <div className="hidden md:flex flex-shrink-0 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-5 sm:py-4">
        <div className="flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
           {/* Bouton retour avec design moderne */}
           <button
              type="button"
              className="group cursor-pointer rounded-xl bg-gray-100 p-2.5 transition-all duration-200 hover:bg-gray-200 sm:p-3"
              onClick={() => navigate("/dashboard")}
              title={t("common.backToDashboard")}
            >
              <img 
                src={Return} 
                alt="return" 
                className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" 
              />
            </button>
          <div className="flex w-full items-center gap-2 overflow-x-auto pb-1 lg:w-auto lg:pb-0">
            <span className="flex-shrink-0 text-sm font-medium text-gray-600">{t("common.sortBy")}</span>
            {/* Bouton de tri par service */}
            <button 
              onClick={() => handleCustomSort("category")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "category" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <span>{t("common.service")}</span>
              {getSortIcon("category")}
            </button>
            
            {/* Bouton de tri par nom */}
            <button 
              onClick={() => handleCustomSort("name")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "name" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <span>{t("common.name")}</span>
              {getSortIcon("name")}
            </button>
            
            {/* Bouton de tri par responsable */}
            <button 
              onClick={() => handleCustomSort("fullName")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "fullName" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <span>{t("common.manager")}</span>
              {getSortIcon("fullName")}
            </button>
            
            {/* Bouton de tri par date */}
            <button 
              onClick={() => handleCustomSort("dueDate")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "dueDate" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <span>{t("common.dueDate")}</span>
              {getSortIcon("dueDate")}
            </button>
          </div>
        </div>
      </div>

      {/* Table des missions */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-3">
        {/* En-têtes de colonnes */}
        <div className="sticky top-0 z-10 hidden w-full grid-cols-7 border-b border-gray-200 bg-white px-6 py-4 text-sm font-semibold text-gray-700 md:grid">

          <div className="col-start-1 col-end-2">{t("common.service")}</div>
          <div className="col-start-2 col-end-4 flex items-center">
            {t("common.name")}
          </div>
          <div className="col-start-4 col-end-5 flex items-center">
            {t("common.manager")}
          </div>
          <div className="col-start-5 col-end-6 flex items-center">
            {t("common.dueDate")}
          </div>
          <div className="col-start-6 col-end-8 flex items-center">
            {t("status.column")}
          </div>
        </div>

        {/* Corps de la table */}
        <div className="w-full">
          {sortedData?.length === 0 || sortedData === undefined ? (
            <div className="text-center py-16 px-6">
              <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t("customerHome.noMissionTitle")}</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {t("customerHome.noMissionText")}
              </p>
            </div>
          ) : (
            sortedData?.map((mission, index) => (
              <div
                key={mission?.id}
                className={`w-full hover:bg-blue-50 transition-all duration-200 border-b border-gray-100 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                <button
                  type="button"
                  className="flex w-full cursor-pointer flex-col gap-3 p-4 text-left transition-colors duration-200 hover:bg-blue-50 md:grid md:grid-cols-7 md:gap-0"
                  onClick={() => {
                    setIsRenewable(mission?.isRenewable);
                    navigate(
                      `/customer/${idCustomer}/missions/${mission?.id}/requests`
                    );
                  }}
                >
                  <div className="hidden items-center p-2 md:col-start-1 md:col-end-2 md:flex">
                      {mission?.category} {mission?.nameMissionPeriod}
                  </div>
                  <div className="flex items-center md:col-start-2 md:col-end-4">
                    <div className="font-medium text-gray-900">
                      {mission?.name && splitMissionName(mission?.name)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-500 md:hidden">
                    <span className="truncate">{mission?.category} {mission?.nameMissionPeriod}</span>
                    <span className="text-right">{dateFormat(mission?.dueDate)}</span>
                    <span className="col-span-2 truncate">
                      {mission?.fullName === " " ? mission.eMail : mission.fullName}
                    </span>
                  </div>
                  <div className="hidden items-center md:col-start-4 md:col-end-5 md:flex">
                    <div className="text-gray-600 text-sm">
                      {mission?.fullName === " " ? mission.eMail : mission.fullName}
                    </div>
                  </div>
                  <div className="hidden items-center md:col-start-5 md:col-end-6 md:flex">
                    <div className="text-gray-600 text-sm">
                      {dateFormat(mission?.dueDate)}
                    </div>
                  </div>
                  <div className="flex items-center md:col-start-6 md:col-end-8">
                    <div className="grid w-full grid-cols-4 gap-2 sm:gap-3">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2"></div>
                        <span className="text-xs text-gray-600 font-medium">
                          {mission?.DocRequests[0] &&
                          Object.keys(mission.DocRequests[0])[0] === "En Attente"
                            ? mission.DocRequests[0]["En Attente"]
                            : 0}
                        </span>
                      </div>
                      <div className="flex  items-center">
                        <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                        <span className="text-xs text-gray-600 font-medium">
                          {mission?.DocRequests[3] &&
                          Object.keys(mission.DocRequests[3])[0] === "En retard"
                            ? mission.DocRequests[3]["En retard"]
                            : 0}
                        </span>
                      </div>
                      <div className="flex  items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-400 mr-2"></div>
                        <span className="text-xs text-gray-600 font-medium">
                          {mission?.DocRequests[1] &&
                          Object.keys(mission.DocRequests[1])[0] === "Délivrée"
                            ? mission.DocRequests[1]["Délivrée"]
                            : 0}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-400 mr-2"></div>
                        <span className="text-xs text-gray-600 font-medium">
                          {mission?.DocRequests[2] &&
                          Object.keys(mission.DocRequests[2])[0] === "Accepté"
                            ? mission.DocRequests[2]["Accepté"]
                            : 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerMissionTable;
